import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import {
  IndexedNoteSyncUpdateValue,
  NoteUpsertedSyncUpdateValue,
  INoteObservable,
  NoteObservable,
  NotesSearchParams,
  NotesIndexTuple,
  NotesSearchMentionedInParams,
  SpaceAccountNoteSuggestion,
  AutoStartVoice,
  NotePasteClipboardContent,
} from "@/store/note";
import { uuidModule } from "@/modules/uuid";
import { NoteModelData } from "@/store/note/types";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import {
  CreateNoteOperation,
  INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT,
} from "@/store/sync/operations/notes/CreateNoteOperation";
import { DeleteNoteOperation } from "@/store/sync/operations/notes/DeleteNoteOperation";
import {
  SerializedOptimisticUpdate,
  SyncModelKind,
  SyncUpdate,
  SyncUpdateValue,
} from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  ObservableMap,
  onBecomeObserved,
  onBecomeUnobserved,
  override,
  reaction,
  runInAction,
} from "mobx";
import Dexie, { liveQuery, Subscription, Table } from "dexie";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { NoteIndexes } from "@/store/note/NoteIndexes";
import { resolveSpaceAccountNoteSyncModelUuid } from "@/modules/uuid/sync-models/resolveSpaceAccountNoteSyncModelUuid";
import { SpaceAccountNoteModelData } from "@/store/recent-items/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { EventContext } from "@/domains/metrics/context";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { PaywallContext } from "@/domains/metrics/tracked-event";
import { NoteQueueObservable } from "@/store/note/NoteQueueObservable";
import { SortByKind } from "@/modules/lenses/types";
import { searchForNotes } from "@/store/note/NoteSearch";
import { searchForNotesMentionedIn } from "@/store/note/searchForNotesMentionedIn";
import {
  generateSpaceAccountNoteLiveSyncUpdatePusherChannelKey,
  waitForSyncOperationToMoveToPending,
} from "@/store/sync/utils";
import { Channel } from "pusher-js";
import { PusherEventData, PusherEventKind } from "@/domains/pusher/constants";
import { debounce } from "lodash-es";
import { withDexieErrorHandling } from "@/domains/dexie-db/dbErrorHandling";
import { DateTime, Duration } from "luxon";
import { GatedFeature } from "@/store/feature-gates/types";
import {
  buildFeatureGateDeniedEventPayload,
  buildMonetizationUsagePayload,
} from "@/domains/metrics/monetization";
import { FeatureGateError } from "@/domains/errors/kinds/FeatureGateError";
import { createNoteEncodedContentForAutoStartVoice } from "@/store/note/helpers/auto-start-voice";
import { createNoteEncodedContentFromTitle } from "@/store/note/helpers/create-note-content-from-title";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { yjsModule } from "@mem-labs/common-editor";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { CreateNoteUsingCalendarEventOperation } from "@/store/sync/operations/calendars/CreateNoteUsingCalendarEventOperation";
import { LinkNoteToCalendarEventOperation } from "@/store/sync/operations/calendars/LinkNoteToCalendarEventOperation";
import { SmartViewKind } from "@/domains/smart/constants";
import {
  CommercialMeterKey,
  CommercialUsageRecoveryAction,
  CommercialUsageStatus,
} from "@/modules/commercial-plans";

export interface NotesStore {
  allNotes: INoteObservable[];
  createNote: (payload: {
    noteId: Uuid;
    eventContext: EventContext;
    paywallContext?: PaywallContext;
  }) => Promise<void>;
  getNoteObservableById: (payload: { noteId: Uuid }) => Maybe<INoteObservable>;
  search: (payload: NotesSearchParams) => Promise<NotesIndexTuple[]>;
}

export class NoteCreateGateDeniedError extends FeatureGateError {
  constructor({
    used,
    limit,
    paywallContext,
  }: {
    used: number;
    limit: number | typeof Infinity;
    paywallContext: PaywallContext;
  }) {
    super({
      feature: GatedFeature.NoteCreate,
      used,
      limit,
      paywallContext,
      message: "Note creation limit reached",
    });
  }
}

export class AppStoreNoteStore
  extends BaseSyncModelStore<NoteObservable, NoteModelData>
  implements NotesStore
{
  public static searchTitleLimit = 100;

  private queues = new ObservableMap<Uuid, NoteQueueObservable>();

  spaceAccountNoteSuggestionsPusherChannels = new ObservableMap<Uuid, Channel>(); // noteId -> Pusher Channel
  spaceAccountNoteSuggestions = new ObservableMap<Uuid, SpaceAccountNoteSuggestion[]>();

  public newNoteIsBeingCreated = false;
  public autoDeletableNoteIds = new Set<Uuid>();

  /** Whether the account has any untrashed notes; null until the first query result. */
  public hasAvailableNotes: boolean | null = null;
  private hasAvailableNotesSubscription: Subscription | null = null;
  private hasAvailableNotesReadyReactionDisposer: IReactionDisposer | null = null;

  public static mentionedNoteIdsIndex = "mentioned_note_ids";
  public static sortByIndexes = {
    [SortByKind.LastViewed]: "last_viewed_at",
    [SortByKind.LastCreated]: "locally_created_at",
    [SortByKind.LastModified]: "modified_at",
    [SortByKind.LastShared]: "shared_with_me_at",
    [SortByKind.Alphabetical]: "lowercase_primary_label",
    [SortByKind.LastInteracted]: "last_interacted_at",
  };

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.Note, ...injectedDeps });
    makeObservable<
      this,
      | "queues"
      | "updateSearchSuggestions"
      | "generateSearchSuggestions"
      | "maybeTriggerMeetingBriefing"
      | "waitForMeetingBriefingTriggerOperation"
      | "markLoadedNoteAsDeleted"
      | "hasAvailableNotesSubscription"
      | "hasAvailableNotesReadyReactionDisposer"
      | "initializeHasAvailableNotesTracking"
      | "subscribeToHasAvailableNotes"
      | "ensureCommercialNewNoteCapacity"
      | "createAdmittedNote"
    >(this, {
      autoDeletableNoteIds: false,
      searchForNotesMentionedIn: true,
      // App/react lifecycle
      resetState: false,

      // OVERRIDES
      processSyncUpdate: override,
      dryProcessSyncUpdate: override,

      // Queues
      queues: false,
      beforeUnload: false,
      getNoteQueue: action,

      remoteTable: override,
      localTable: override,
      recompute: override,
      bulkRecompute: override,

      createSyncModel: false,
      getNoteObservableById: false,
      fetch: action,
      ensureCommercialNewNoteCapacity: false,
      createAdmittedNote: false,

      allNotes: computed,

      composeNewNote: action,
      createNote: action,
      createNoteUsingCalendarEventId: false,
      linkNoteToCalendarEvent: action,
      maybeTriggerMeetingBriefing: false,
      waitForMeetingBriefingTriggerOperation: false,
      deleteNote: action,

      updateSearchSuggestions: false,
      generateSearchSuggestions: false,
      markLoadedNoteAsDeleted: false,

      search: false,
      countMyNotesCreatedSince: false,

      newNoteIsBeingCreated: observable,
      setNewNoteIsBeingCreated: action,
      hasAvailableNotes: observable,
      hasAvailableNotesSubscription: false,
      hasAvailableNotesReadyReactionDisposer: false,
      initializeHasAvailableNotesTracking: false,
      subscribeToHasAvailableNotes: false,
      // SpaceAccountNoteSuggestion Subscriptions
      spaceAccountNoteSuggestionsPusherChannels: observable,
      spaceAccountNoteSuggestions: observable,
      subscribeToLiveNoteSuggestions: action,
      unsubscribeFromLiveNoteSuggestions: action,
      handleSpaceAccountNoteSuggestion: action,
      removeSuggestion: action,
      queuesAreActive: computed,
    });

    window.addEventListener("beforeunload", this.beforeUnload);

    this.initializeHasAvailableNotesTracking();
  }

  private initializeHasAvailableNotesTracking() {
    onBecomeObserved(this, "hasAvailableNotes", () => {
      this.hasAvailableNotesReadyReactionDisposer?.();
      this.hasAvailableNotesReadyReactionDisposer = reaction(
        () => this.store.isMemDbReady,
        isReady => {
          if (isReady) {
            this.subscribeToHasAvailableNotes();
            this.hasAvailableNotesReadyReactionDisposer?.();
            this.hasAvailableNotesReadyReactionDisposer = null;
          }
        },
        { fireImmediately: true }
      );
    });

    onBecomeUnobserved(this, "hasAvailableNotes", () => {
      this.hasAvailableNotesSubscription?.unsubscribe();
      this.hasAvailableNotesSubscription = null;

      this.hasAvailableNotesReadyReactionDisposer?.();
      this.hasAvailableNotesReadyReactionDisposer = null;
    });
  }

  private subscribeToHasAvailableNotes() {
    this.hasAvailableNotesSubscription?.unsubscribe();
    this.hasAvailableNotesSubscription = liveQuery(() =>
      this.localTable.where("is_available").equals(1).limit(1).count()
    ).subscribe({
      next: count => {
        runInAction(() => {
          this.hasAvailableNotes = count > 0;
        });
      },
      error: () => {
        runInAction(() => {
          this.hasAvailableNotes = null;
        });
      },
    });
  }

  beforeUnload = (e: Event) => {
    for (const [, queue] of this.queues) {
      if (queue.isEmpty) continue;

      e.preventDefault();
      e.stopPropagation();

      return "Changes are being saved. Are you sure you want to leave?";
    }
  };

  createSyncModel(updateValue: IndexedNoteSyncUpdateValue): NoteObservable {
    return new NoteObservable({ id: updateValue.model_id, data: updateValue, store: this.store });
  }

  // OVERRIDES

  public async processSyncUpdate(
    update: SyncUpdate<NoteModelData>,
    _options: { hydrating?: boolean } = {}
  ) {
    await super.processSyncUpdate(update, _options);
  }

  public dryProcessSyncUpdate(update: SyncUpdate<NoteModelData>): boolean {
    return super.dryProcessSyncUpdate(update);
  }

  getNoteObservableById = ({ noteId }: { noteId?: Uuid }): Maybe<INoteObservable> => {
    return noteId ? this.get(noteId) : undefined;
  };

  getNoteQueue = ({ noteId }: { noteId: Uuid }): NoteQueueObservable => {
    let queue = this.queues.get(noteId);
    if (!queue) {
      queue = new NoteQueueObservable(this.store, noteId);
      this.queues.set(noteId, queue);
    }
    // Idea:
    //     We could use the queue on recompute + call recompute when reacting to a new operation
    //     on the queue.
    // Concerns:
    // 1. Order of operations read from disk vs those still only kept in memory.
    //    One possibility is to process disk operations and then memory ops, giving them
    //    higher priority.
    // 2. This only covers operations saved to the note queue (mostly from the editor).
    //    Since they are never queued in memory but written to disk and then they are loaded to
    //    the queue maybe it's not such a big issue.
    // 3. Collections and other models are not covered.
    //    To fix those we would need queues like NoteQueueObservable for each model id or to
    //    bring back an unified memory queue (much more complex).
    // Overall:
    //    Doing it for notes seems relevant, for other entities perhaps the complexity is not worth it.
    return queue;
  };

  get queuesAreActive(): boolean {
    for (const [, queue] of this.queues) {
      if (queue.isProcessing || queue.operations.length > 0) return true;
    }
    return false;
  }

  get allNotes(): INoteObservable[] {
    // DEXIE REFACTOR TODO: REFACTOR FOR SEARCH
    return [];
  }

  // ACTIONS
  public composeNewNote = async ({
    eventContext,
    noteId: providedNoteId,
    noteContent: providedNoteContent,
    autoFocusPosition = "start",
    autoStartVoice,
    onBeforeCreate,
    onCreateError,
    calendarEvent,
    clipboardEvent,
    title,
  }: {
    eventContext: EventContext;
    noteId?: Uuid;
    noteContent?: { encodedContent: string; primaryLabel: string; secondaryLabel: string };
    autoStartVoice?: AutoStartVoice;
    autoFocusPosition?: "start" | "end";
    onBeforeCreate?: () => void;
    onCreateError?: () => void;
    calendarEvent?: SpaceAccountCalendarEventObservable;
    clipboardEvent?: ClipboardEvent;
    title?: string;
  }): Promise<Uuid | undefined> => {
    if (this.newNoteIsBeingCreated && autoStartVoice) return;

    let noteId: Uuid | undefined;
    let didNavigateToNote = false;

    try {
      noteId = providedNoteId ?? uuidModule.generate();
      const resolvedAutoFocusPosition = clipboardEvent ? "end" : autoFocusPosition;

      this.setNewNoteIsBeingCreated(true);

      let initialPasteClipboardContent: NotePasteClipboardContent | undefined;

      const navigateToCreatedNote = () => {
        if (didNavigateToNote || !noteId) return;

        didNavigateToNote = true;
        this.store.navigation.goToNote({
          noteId,
          autoFocus: true,
          autoFocusPosition: resolvedAutoFocusPosition,
          skipEditorOpeningAnimation: true,
          autoStartVoice,
          initialPasteClipboardContent,
        });
      };

      if (clipboardEvent?.clipboardData) {
        const text = clipboardEvent.clipboardData.getData("text/plain");
        const html = clipboardEvent.clipboardData.getData("text/html");
        initialPasteClipboardContent = { noteId, text, html };
      }

      const noteContent =
        providedNoteContent ??
        (title
          ? createNoteEncodedContentFromTitle(title)
          : createNoteEncodedContentForAutoStartVoice(autoStartVoice));

      await this.createNote({
        noteId,
        calendarEvent,
        eventContext,
        noteContent,
        onEditorReady: navigateToCreatedNote,
        onBeforeCreate,
        onCreateError,
      });

      navigateToCreatedNote();

      // escape-hatch in case user run to non-editor page
      setTimeout(() => this.setNewNoteIsBeingCreated(false), 2000);

      return noteId;
    } catch (e) {
      this.setNewNoteIsBeingCreated(false);
      if (e instanceof NoteCreateGateDeniedError) {
        return;
      }
      logger.error({
        message: "[SYNC][AppStoreNoteStore] Error creating new note",
        info: { error: objectModule.safeErrorAsJson(e as Error) },
      });
      return;
    }
  };

  async createNoteUsingCalendarEventId({
    noteId,
    calendarEventId,
  }: {
    noteId: Uuid;
    calendarEventId: Uuid;
  }) {
    const calendarEvent = await this.store.spaceAccountCalendarEvents.getAsync(calendarEventId);
    if (!calendarEvent) return;

    await this.composeNewNote({
      eventContext: EventContext.CalendarEvent,
      noteId,
      calendarEvent,
      autoFocusPosition: "end",
    });
  }

  async linkNoteToCalendarEvent({
    noteId,
    calendarEventId,
  }: {
    noteId: Uuid;
    calendarEventId: Uuid;
  }) {
    const calendarEvent = await this.store.spaceAccountCalendarEvents.getAsync(calendarEventId);
    if (!calendarEvent) return;

    const linkNoteToCalendarEventOperation = new LinkNoteToCalendarEventOperation({
      store: this.store,
      space_account_calendar_event_id: calendarEventId,
      payload: {
        note_id: noteId,
        connected_calendar_event_id: calendarEvent.associatedConnectedCalendarEventId,
      },
    });
    await linkNoteToCalendarEventOperation.execute();

    void this.maybeTriggerMeetingBriefing({
      activeNoteId: noteId,
      connectedCalendarEventId: calendarEvent.associatedConnectedCalendarEventId,
      syncOperationId: linkNoteToCalendarEventOperation.id,
    });
  }

  private async waitForMeetingBriefingTriggerOperation({
    syncOperationId,
  }: {
    syncOperationId: Uuid;
  }): Promise<void> {
    try {
      await waitForSyncOperationToMoveToPending({
        syncStore: this.store.sync,
        operationId: syncOperationId,
        timeout: Duration.fromObject({ seconds: 10 }),
      });
    } catch {
      // Continue; the meeting briefing request should still run if sync wait times out.
    }
  }

  private async maybeTriggerMeetingBriefing({
    activeNoteId,
    connectedCalendarEventId,
    syncOperationId,
  }: {
    activeNoteId: Uuid;
    connectedCalendarEventId?: Uuid | null;
    syncOperationId?: Uuid | null;
  }): Promise<void> {
    if (!connectedCalendarEventId) return;
    const isPaid = this.store.subscriptions.featureAccess?.isPaid ?? false;
    if (!isPaid) return;

    if (syncOperationId) {
      await this.waitForMeetingBriefingTriggerOperation({
        syncOperationId,
      });
    }

    await this.store.headsUp
      .requestMeetingBriefingForContext({
        activeNoteId,
        connectedCalendarEventId,
        forceGenerate: true,
      })
      .catch(() => {});

    await this.store.headsUp.generateForContext({
      activeNoteId,
      smartViewKind: SmartViewKind.HeadsUp,
      suppressLoadingState: true,
    });
  }

  public async createNote({
    noteId,
    calendarEvent,
    eventContext,
    paywallContext = PaywallContext.NoteCreate,
    noteContent,
    onEditorReady,
    onBeforeCreate,
    onCreateError,
  }: {
    noteId: Uuid;
    calendarEvent?: SpaceAccountCalendarEventObservable;
    eventContext: EventContext;
    paywallContext?: PaywallContext;
    noteContent?: { encodedContent: string; primaryLabel: string; secondaryLabel: string };
    onEditorReady?: () => void;
    onBeforeCreate?: () => void;
    onCreateError?: () => void;
  }) {
    const usesCommercialNoteAdmission = await this.ensureCommercialNewNoteCapacity({
      paywallContext,
    });

    if (!usesCommercialNoteAdmission) {
      try {
        const gate = this.store.featureGates.checkGate(GatedFeature.NoteCreate);
        if (!gate.allowed) {
          trackEvent(
            TrackedEvent.FeatureGateDenied,
            buildFeatureGateDeniedEventPayload({
              feature: GatedFeature.NoteCreate,
              context: paywallContext,
              gate,
            })
          );
          this.store.featureGates.openUsageLimitUpsell({
            used: gate.used,
            limit: gate.limit as number,
            feature: GatedFeature.NoteCreate,
            context: paywallContext,
          });
          throw new NoteCreateGateDeniedError({
            used: gate.used,
            limit: gate.limit,
            paywallContext,
          });
        }
      } catch (error) {
        if (error instanceof NoteCreateGateDeniedError) throw error;

        logger.error({
          message: "[SYNC][AppStoreNoteStore] Error checking note create gate",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });

        throw error;
      }
    }

    return this.createAdmittedNote({
      noteId,
      calendarEvent,
      eventContext,
      noteContent,
      onEditorReady,
      onBeforeCreate,
      onCreateError,
    });
  }

  /**
   * Refreshes an obvious at-limit snapshot before preventing editor creation.
   *
   * A snapshot that says capacity remains is only a fast path; the submit API
   * still makes the authoritative race-time decision. If refresh fails, the
   * client also submits instead of rejecting from stale cached usage.
   */
  private async ensureCommercialNewNoteCapacity({
    paywallContext,
  }: {
    paywallContext: PaywallContext;
  }): Promise<boolean> {
    if (this.store.platformInfo.commercialCatalog?.usageEnforcementEnabled !== true) {
      return false;
    }

    const snapshot = this.store.platformInfo.commercialPlanSnapshot;
    if (!snapshot || snapshot.currentPlan.isLegacy) {
      return false;
    }

    const meter = snapshot.meters[CommercialMeterKey.NewNotes];
    if (meter.status !== CommercialUsageStatus.AtLimit) {
      return true;
    }

    await this.store.platformInfo.fetchPlatformInfo();

    if (this.store.platformInfo.fetchPlatformInfoState.data !== true) {
      return true;
    }

    const refreshedSnapshot = this.store.platformInfo.commercialPlanSnapshot;
    if (!refreshedSnapshot || refreshedSnapshot.currentPlan.isLegacy) {
      return false;
    }

    const refreshedMeter = refreshedSnapshot.meters[CommercialMeterKey.NewNotes];
    if (refreshedMeter.status !== CommercialUsageStatus.AtLimit) {
      return true;
    }

    const recoveryActions: CommercialUsageRecoveryAction[] = [
      CommercialUsageRecoveryAction.ReviewPlans,
    ];
    if (refreshedSnapshot.blockPolicy?.available) {
      recoveryActions.unshift(CommercialUsageRecoveryAction.ReviewExtraUsage);
    }

    this.store.subscriptions.openCommercialUsageLimit({
      accepted: false,
      code: "COMMERCIAL_USAGE_LIMIT_REACHED",
      meterKey: CommercialMeterKey.NewNotes,
      recoveryActions,
    });
    throw new NoteCreateGateDeniedError({
      used: refreshedMeter.used,
      limit: refreshedMeter.allowance ?? Infinity,
      paywallContext,
    });
  }

  private async createAdmittedNote({
    noteId,
    calendarEvent,
    eventContext,
    noteContent,
    onEditorReady,
    onBeforeCreate,
    onCreateError,
  }: {
    noteId: Uuid;
    calendarEvent?: SpaceAccountCalendarEventObservable;
    eventContext: EventContext;
    noteContent?: { encodedContent: string; primaryLabel: string; secondaryLabel: string };
    onEditorReady?: () => void;
    onBeforeCreate?: () => void;
    onCreateError?: () => void;
  }) {
    let meetingBriefingSyncOperationId: Uuid | null = null;

    // Only notes created in the current "session" can be automatically deleted.
    // This prevents the deletion of voice recording happening on different clients.
    this.autoDeletableNoteIds.add(noteId);

    onBeforeCreate?.();

    const templateResult = calendarEvent
      ? await calendarEvent.getTemplateIdAndEncodedContentAsync().catch(err => {
          logger.error({
            message: "[AppStoreNoteStore] Error getting template id and encoded content",
            info: {
              error: objectModule.safeErrorAsJson(err as Error),
              calendarEventId: calendarEvent.id,
            },
          });
        })
      : undefined;

    try {
      if (templateResult) {
        const createNoteUsingCalendarEventOperation = new CreateNoteUsingCalendarEventOperation({
          store: this.store,
          payload: {
            note_id: noteId,
            connected_calendar_event_id: calendarEvent!.associatedConnectedCalendarEventId,
            template_id: templateResult.templateId,
            encoded_content: templateResult.encodedContent,
          },
        });
        await createNoteUsingCalendarEventOperation.execute();
        meetingBriefingSyncOperationId = createNoteUsingCalendarEventOperation.id;
      } else {
        const createNoteOperation = new CreateNoteOperation({
          store: this.store,
          payload: { id: noteId },
        });
        createNoteOperation.primeOptimisticModelsForEditor();
        onEditorReady?.();
        await createNoteOperation.execute();
        if (noteContent) {
          // This is less efficient than a single "create with encoded content" operation.
          const diff = yjsModule.updateEncodedTipTapContentUsingFullState({
            encodedContent: noteContent.encodedContent,
            previousEncodedContent: INITIAL_NOTE_CONTENT_DOCUMENT_ENCODED_CONTENT,
          });
          await new UpdateNoteContentUsingDiffOperation({
            store: this.store,
            payload: { id: noteId, encoded_content_diff: diff },
            primaryLabel: noteContent.primaryLabel,
            secondaryLabel: noteContent.secondaryLabel,
          }).execute();
        }
      }
    } catch (error) {
      onCreateError?.();
      throw error;
    }

    if (calendarEvent?.associatedConnectedCalendarEventId) {
      void this.maybeTriggerMeetingBriefing({
        activeNoteId: noteId,
        connectedCalendarEventId: calendarEvent.associatedConnectedCalendarEventId,
        syncOperationId: meetingBriefingSyncOperationId,
      });
    }

    this.store.headsUp.deferAutoGenerationUntilNoteReady(noteId);

    try {
      const monetizationProperties = buildMonetizationUsagePayload(this.store);
      trackEvent(TrackedEvent.NoteCreate, {
        note_id: noteId,
        context: eventContext,
        ...monetizationProperties,
      });
    } catch {
      // fallback to basic event if gate check fails
      trackEvent(TrackedEvent.NoteCreate, { note_id: noteId, context: eventContext });
    }
  }

  public async deleteNote({ noteId }: { noteId: Uuid }) {
    const queue = this.store.notes.getNoteQueue({ noteId });
    queue.push(new DeleteNoteOperation({ store: this.store, payload: { id: noteId } }));
  }

  public get remoteTable() {
    return this.db.mappedTables[this.modelKind].remote as Table<SyncUpdateValue<NoteModelData>>;
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind].local as Table<IndexedNoteSyncUpdateValue>;
  }

  private markLoadedNoteAsDeleted(modelId: Uuid) {
    const loadedNote = this.pool.get(modelId)?.deref();
    if (loadedNote) loadedNote.isDeleted = true;
    this.pool.delete(modelId);
  }

  public async recompute(modelId: Uuid) {
    const remoteData = await this.remoteTable.get(modelId);
    const optimisticUpdates = await this.db.queue.optimisticUpdates
      .where({ model_id: modelId })
      .sortBy("locally_committed_at");

    const lastOptimisticUpdate = optimisticUpdates.at(-1);
    if (lastOptimisticUpdate?.kind === "DELETED" || lastOptimisticUpdate?.kind === "ACL_REVOKED") {
      logger.debug({
        message: "[APP STORE NOTE STORE] deleting note / revoking access",
        info: { modelId },
      });

      await this.localTable.delete(modelId);
      runInAction(() => this.markLoadedNoteAsDeleted(modelId));
      await this.store.search.remove(modelId);

      return;
    }

    // Fetch other required data
    const spaceAccountNoteId = resolveSpaceAccountNoteSyncModelUuid({
      noteId: modelId,
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
    });

    const spaceAccountNote = (await this.db.mappedTables[SyncModelKind.SpaceAccountNote].local.get(
      spaceAccountNoteId
    )) as SyncUpdateValue<SpaceAccountNoteModelData> | undefined;

    // Optimistic updates
    const data = lastOptimisticUpdate?.value || remoteData; // At least one of these should be defined
    if (!data) {
      await this.localTable.delete(modelId);
      runInAction(() => this.markLoadedNoteAsDeleted(modelId));
      await this.store.search.remove(modelId);
      return;
    }

    const indexes = new NoteIndexes({
      store: this.store,
      remoteData,
      optimisticUpdates,
      spaceAccountNote,
    }).indexes;

    const dataWithIndexes: IndexedNoteSyncUpdateValue = {
      ...(data as NoteUpsertedSyncUpdateValue),
      ...indexes,
    };

    await withDexieErrorHandling(
      async () => {
        await this.localTable.put(dataWithIndexes, dataWithIndexes.model_id);
      },
      this.store,
      `[AppStoreNoteStore] recompute`
    );

    // NOTE: we update fullText search content not here but in the NoteContentDocumentStore to
    // a. detach its potentially heavy computation from note content update
    // b. get a direct access to the most up-to-date note content (we don't optimistically update note content)
    await this.updateSearchSuggestions(dataWithIndexes);
  }

  public override async bulkRecompute(modelIds: Uuid[]) {
    const remoteData = (await this.remoteTable.bulkGet(modelIds)) as (
      | SyncUpdateValue<NoteModelData>
      | undefined
    )[];

    const allOptimisticUpdates = await this.db.queue.optimisticUpdates
      .where("model_id")
      .anyOf(modelIds)
      .sortBy("locally_committed_at");
    const optimisticUpdatesById = new Map<Uuid, SerializedOptimisticUpdate<NoteModelData>[]>();
    for (const update of allOptimisticUpdates) {
      optimisticUpdatesById.set(update.model_id, [
        ...(optimisticUpdatesById.get(update.model_id) || []),
        update,
      ]);
    }

    const noteIdsToSpaceAccountNoteIds = new Map(
      modelIds.map(modelId => [
        modelId,
        resolveSpaceAccountNoteSyncModelUuid({
          noteId: modelId,
          spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
        }),
      ])
    );
    const spaceAccountNoteIds = Array.from(noteIdsToSpaceAccountNoteIds.values());
    const spaceAccountNotes = (await this.db.mappedTables[
      SyncModelKind.SpaceAccountNote
    ].local.bulkGet(spaceAccountNoteIds)) as (
      | SyncUpdateValue<SpaceAccountNoteModelData>
      | undefined
    )[];
    const noteIdsToSpaceAccountNotes: Map<
      Uuid,
      SyncUpdateValue<SpaceAccountNoteModelData> | undefined
    > = new Map(
      spaceAccountNoteIds.map((_spaceAccountNoteId, i) => [modelIds[i], spaceAccountNotes[i]])
    );

    const toDelete: Uuid[] = [];
    const toPut: IndexedNoteSyncUpdateValue[] = [];
    const searchSuggestions: SearchSuggestion[] = [];

    for (const [i, modelId] of modelIds.entries()) {
      const spaceAccountNote = noteIdsToSpaceAccountNotes.get(modelId);
      const optimisticUpdates = optimisticUpdatesById.get(modelId) || [];
      const lastOptimisticUpdate = optimisticUpdates.at(-1);

      if (
        lastOptimisticUpdate?.kind === "DELETED" ||
        lastOptimisticUpdate?.kind === "ACL_REVOKED"
      ) {
        toDelete.push(modelId);
        continue;
      }

      const data = (lastOptimisticUpdate?.value || remoteData[i]) as
        | SyncUpdateValue<NoteModelData>
        | undefined;

      if (!data) {
        // Case: remoteData is deleted and there are no optimistic updates\
        toDelete.push(modelId);
        continue;
      }

      const indexes = new NoteIndexes({
        store: this.store,
        remoteData: data,
        optimisticUpdates,
        spaceAccountNote,
      }).indexes;

      const dataWithIndexes: IndexedNoteSyncUpdateValue = {
        ...(data as NoteUpsertedSyncUpdateValue),
        ...indexes,
      };

      toPut.push(dataWithIndexes);
      searchSuggestions.push(this.generateSearchSuggestions(dataWithIndexes));
    }

    await this.db.transaction("rw", this.localTable, async () => {
      console.debug("[AppStoreNoteStore] bulkRecompute delete", { ids: toDelete });
      await this.localTable.bulkDelete(toDelete);
      console.debug("[AppStoreNoteStore] bulkRecompute put", { ids: toPut.map(p => p.model_id) });
      await this.localTable.bulkPut(toPut);
      console.debug("[AppStoreNoteStore] bulkRecompute commit");
    });

    await this.store.search.bulkUpdateSuggestions(searchSuggestions);

    runInAction(() => {
      for (const modelId of toDelete) {
        this.markLoadedNoteAsDeleted(modelId);
      }
      // this is a noop update to retrigger get for observables that previously returned undefined in the case they they become defined through the recompute
      for (const data of toPut) {
        const modelId = data.model_id;
        if (!this.pool.has(modelId)) {
          this.pool.set(modelId, undefined);
          this.pool.delete(modelId);
        }
      }
    });
  }

  public search = (params: NotesSearchParams) => searchForNotes({ store: this.store, params });

  public searchForNotesMentionedIn = (params: NotesSearchMentionedInParams) =>
    searchForNotesMentionedIn({ store: this.store, params });

  /** **FEATURE GATES SHOULD BE INSTANTANEOUS, USE THE COMPUTED VALUE!!!** */
  public async countMyNotesCreatedSince(createdSince: DateTime): Promise<number> {
    const noteTable = this.localTable;
    const createdSinceISO = createdSince.toISO();
    // Use compound index with import_batch_id BEFORE quota_created_at to correctly filter imported notes
    // import_batch_id is normalized to "" for non-imported notes (see NoteIndexes.import_batch_id)
    // Index order: [is_available+is_owned_by_me+import_batch_id+quota_created_at+model_id]
    // This allows: exact match on is_available=1, is_owned_by_me=1, import_batch_id="", then range on quota_created_at
    const compoundIndex = "[is_available+is_owned_by_me+import_batch_id+quota_created_at+model_id]";
    return await noteTable
      .where(compoundIndex)
      .between([1, 1, "", createdSinceISO], [1, 1, "", Dexie.maxKey])
      .count();
  }

  public setNewNoteIsBeingCreated = (value: boolean) => {
    this.newNoteIsBeingCreated = value;
  };

  private generateSearchSuggestions(value: IndexedNoteSyncUpdateValue): SearchSuggestion {
    const generateSortKey = (
      value: IndexedNoteSyncUpdateValue
      // includeMentionedAt: boolean
    ): number => {
      const sortKey = value.last_viewed_at || value.created_at;

      // TODO: add lastMentionedAt for includeMentionedAt

      return new Date(sortKey || "").getTime();
    };

    const suggestion = {
      modelId: value.model_id,
      label: value.primary_label.slice(0, AppStoreNoteStore.searchTitleLimit),
      lowercaseLabel: value.primary_label
        .slice(0, AppStoreNoteStore.searchTitleLimit)
        .toLowerCase(),
      type: SearchSuggestionType.NOTE,
      lastViewedAt: value.last_viewed_at,
      sortKey: generateSortKey(value),
      mentionKey: generateSortKey(value),
      isAvailable: value.is_available,
    };

    return suggestion;
  }

  private async updateSearchSuggestions(value: IndexedNoteSyncUpdateValue) {
    try {
      const suggestion = this.generateSearchSuggestions(value);
      await this.store.search.updateSuggestion(suggestion);
    } catch (e) {
      logger.error({
        message: "[SYNC][AppStoreNoteStore] Error generating search suggestion",
        info: { error: objectModule.safeErrorAsJson(e as Error) },
      });
    }
  }

  resetState() {
    this.queues.forEach(queue => queue.dispose());

    window.removeEventListener("beforeunload", this.beforeUnload);
  }

  public subscribeToLiveNoteSuggestions = (noteId: Uuid): boolean => {
    const spaceAccountNoteId = resolveSpaceAccountNoteSyncModelUuid({
      noteId,
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
    });
    const pusherChannelKey = generateSpaceAccountNoteLiveSyncUpdatePusherChannelKey({
      spaceAccountNoteId,
    });

    const liveChannel = this.pusher.subscribe(pusherChannelKey);
    if (!liveChannel.subscribed) return false;
    this.spaceAccountNoteSuggestionsPusherChannels.set(noteId, liveChannel);
    liveChannel.bind(
      PusherEventKind.SPACE_ACCOUNT_NOTE_SUGGESTION_PUBLISHED,
      this.handleSpaceAccountNoteSuggestion
    );
    return true;
  };

  public unsubscribeFromLiveNoteSuggestions = (noteId: Uuid) => {
    const liveChannel = this.spaceAccountNoteSuggestionsPusherChannels.get(noteId);
    if (!liveChannel) return;
    this.pusher.unsubscribe(liveChannel.name);
    this.spaceAccountNoteSuggestionsPusherChannels.delete(noteId);
  };

  public handleSpaceAccountNoteSuggestion = debounce(
    (data: PusherEventData<PusherEventKind.SPACE_ACCOUNT_NOTE_SUGGESTION_PUBLISHED>) => {
      const { note_id, suggestions } = data;
      // Add unique IDs to suggestions if they don't have them
      const suggestionsWithIds = suggestions.map(suggestion => ({
        ...suggestion,
        id: suggestion.id || uuidModule.generate(),
      }));
      this.spaceAccountNoteSuggestions.set(note_id, suggestionsWithIds);
    },
    1000,
    { leading: false, trailing: true }
  );

  public removeSuggestion = (noteId: Uuid, suggestionId: string) => {
    const suggestions = this.spaceAccountNoteSuggestions.get(noteId);
    if (!suggestions) return;

    const updatedSuggestions = suggestions.filter(suggestion => suggestion.id !== suggestionId);
    this.spaceAccountNoteSuggestions.set(noteId, updatedSuggestions);
  };

  async fetch(noteId: Uuid): Promise<boolean> {
    const result = await this.hydrateModels([noteId], {
      retries: 3,
    });

    // If no note updates were returned, the note doesn't exist
    if (result.success && result.updates.length === 0) {
      return false;
    }

    return result.success;
  }
}
